import React, { useMemo, useCallback, useState } from 'react';
import { useQueryStates, parseAsInteger, parseAsString } from 'nuqs';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { fetchSitesMonitoring, SitesSearchDto } from './siteApi';
import { Shield, Cpu } from 'lucide-react';
import { Comparator, FilterOperator, Filter, FilterGroup, SortOrder } from '@/lib/utils';
import { ColumnDef, Updater, VisibilityState  } from '@tanstack/react-table';
import {
  DataTable,
  DataTableContainer,
  DataTableContent,
  DataTableFilterBar,
  DataTableUtilityBar,
  DataTableHeader,
  DataTableRows,
  DataTableWrapper,
  DataTableFooter,
} from '@/components/data-table';
import {
  FilterForm,
  FilterField,
  FilterHeader,
  FilterResetButton,
  FilterInlineResetButton,
} from '@/components/filters/FilterForm';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { EnumAiAutomationLevel, EnumArmProfile, EnumSirenProfile } from '@/lib/api/types.gen';
import { ArmBadge, AiBadge } from '@/components/ui/badges';
import { ScrollArea } from '@/components/ui/scroll-area';

// TypeScript interface for site table data
interface SiteTableData {
  id: string;
  name: string;
  companyId: string;
  companyName: string;
  location: string;
  isActive: boolean;
  status: string;
  archivedAt: string | null;
  armProfile?: EnumArmProfile;
  disarmedUntil?: string | null;
  sirenProfile?: EnumSirenProfile;
  aiAutomationLevel?: EnumAiAutomationLevel;
  isGroupHeader?: boolean;
}

// Extend API row to include optional fields present on backend
interface ExtendedSite extends SitesSearchDto {
  armProfile?: EnumArmProfile;
  disarmedUntil?: string | null;
  sirenProfile?: EnumSirenProfile;
  aiAutomationLevel?: EnumAiAutomationLevel;
}

// Transform API data to table format
function extractSiteTableData(sites: SitesSearchDto[]): SiteTableData[] {
  return sites.map((site) => ({
    id: site.id || '',
    name: site.name || '',
    companyId: site.companyId || '',
    companyName: site.companyName || '',
    location: site.address1 || '',
    isActive: site.isActive || false,
    status: site.isActive ? 'Active' : 'Offline',
    archivedAt: site.archivedAt || null,
    armProfile: (site as ExtendedSite).armProfile,
    disarmedUntil: (site as ExtendedSite).disarmedUntil,
    sirenProfile: (site as ExtendedSite).sirenProfile,
    aiAutomationLevel: (site as ExtendedSite).aiAutomationLevel,
  }));
}

// URL param parsers for sites filters
const sitesUrlParsers = {
  page: parseAsInteger.withDefault(1),
  pageSize: parseAsInteger.withDefault(20),
  name: parseAsString,
  armProfile: parseAsString,
  aiAutomationLevel: parseAsString,
  archived: parseAsString.withDefault('active'),
  filters: parseAsString.withDefault('open'),
};

const Sites: React.FC = () => {
  const navigate = useNavigate();
  const [urlParams, setUrlParams] = useQueryStates(sitesUrlParsers, { history: 'replace' });

  // Derive filter values from URL
  const page = urlParams.page;
  const pageSize = urlParams.pageSize;
  const searchQuery = urlParams.name ?? '';
  const armProfileFilter = (urlParams.armProfile ?? '') as '' | EnumArmProfile;
  const aiAutomationFilter = (urlParams.aiAutomationLevel ?? '') as '' | EnumAiAutomationLevel;
  const archivedFilter = urlParams.archived as 'any' | 'active' | 'archived';
  const filtersOpen = urlParams.filters === 'open';
  const view = 'table' as const;
  const groupByCustomer = false;

  // Build server-side filters (archived handled client-side)
  const filterFilters: Filter[] = useMemo(() => {
    const filters: Filter[] = [];
    if (searchQuery) {
      filters.push({ field: 'name', value: searchQuery, action: Comparator.ILike });
    }
    if (armProfileFilter) {
      filters.push({ field: 'armProfile', value: armProfileFilter, action: Comparator.Eq });
    }
    if (aiAutomationFilter) {
      filters.push({
        field: 'aiAutomationLevel',
        value: aiAutomationFilter,
        action: Comparator.Eq,
      });
    }

    if(archivedFilter === 'active')
    {
      filters.push({ field: 'archivedAt', value: undefined, action: Comparator.Eq });
    }
    else if(archivedFilter === 'archived')
    {
      filters.push({ field: 'archivedAt', value: undefined, action: Comparator.NEq });
    }
    
    return filters;
  }, [searchQuery, armProfileFilter, aiAutomationFilter, archivedFilter]);

  // Query input parameters
  const queryInput = useMemo(
    () => ({
      pageNumber: page,
      pageSize: pageSize,
      sortBy: 'name',
      sortDir: SortOrder.Ascending,
      filterOperator: FilterOperator.And,
      filterFilters: filterFilters.length > 0 ? filterFilters : undefined,
    }),
    [page, pageSize, filterFilters]
  );

  // Use regular useQuery instead of useInfiniteQuery
  const { data, isLoading, error } = useQuery({
    queryKey: ['sites', queryInput],
    queryFn: async () => {
      return await fetchSitesMonitoring(queryInput);
    },
  });

  const sites = data?.sites || [];
  const totalCount = data?.totalRows;

  console.log(sites);

  // Helper renderers
  function renderArmBadge(value?: EnumArmProfile) {
    const label = value ?? 'Unknown';
    const styles =
      label === 'Armed'
        ? 'bg-green-200/20 text-white'
        : label === 'Disarmed'
          ? 'bg-red-300/20 text-white/70'
          : label === 'TestMode'
            ? 'bg-yellow-300/20 text-white/70'
            : 'bg-green-600/20 text-white/70';
    const title =
      label === 'Armed'
        ? 'Site is always armed'
        : label === 'Disarmed'
          ? 'Site is never armed'
          : label === 'TestMode'
            ? 'Testing mode'
            : 'Automatic arming based on rules';
    return (
      <span
        title={title}
        className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${styles}`}
      >
        <Shield className="h-3 w-3" /> {label}
      </span>
    );
  }

  function renderAiBadge(value?: EnumAiAutomationLevel) {
    const level = value ?? EnumAiAutomationLevel.None;
    const styles =
      level === EnumAiAutomationLevel.None
        ? 'bg-red-600/20 text-white/70'
        : level === EnumAiAutomationLevel.Assistant
          ? 'bg-purple-500/20 text-white'
          : 'bg-green-500/20 text-white';
    const title =
      level === EnumAiAutomationLevel.None
        ? 'Manual investigations only'
        : level === EnumAiAutomationLevel.Assistant
          ? 'Assisted AI automation'
          : 'Full AI automation';
    return (
      <span
        title={title}
        className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${styles}`}
      >
        <Cpu className="h-3 w-3" /> {level}
      </span>
    );
  }

  // Column definitions
  const siteColumns: ColumnDef<SiteTableData>[] = [
    {
      id: 'name',
      accessorKey: 'name',
      enableHiding: false,
      meta: { label: 'Site Name' },
      header: 'Site Name',
      enableSorting: false,
      cell: ({ row }) => {
        const { name, id, companyId, isGroupHeader } = row.original;

        // Render group header differently
        if (isGroupHeader) {
          return (
            <div className="flex items-center gap-2 py-2">
              <div className="text-lg font-bold text-lime">{name}</div>
              <div className="h-px flex-1 bg-gray-600" />
            </div>
          );
        }

        return (
          <Link
            to={`/customer/${companyId}/project/${id}`}
            className="ml-4 font-semibold text-lime hover:underline"
          >
            {name}
          </Link>
        );
      },
    },
    {
      id: 'companyName',
      accessorKey: 'companyName',
      meta: { label: 'Customer' },
      header: 'Customer',
      enableSorting: false,
      cell: ({ row }) => {
        const { companyName, isGroupHeader } = row.original;

        // Don't show content for group headers in this column
        if (isGroupHeader) {
          return null;
        }

        return companyName || <span className="text-gray-600">--</span>;
      },
    },
    {
      id: 'armProfile',
      accessorKey: 'armProfile',
      meta: { label: 'Arming Status' },
      header: 'Arming Status',
      enableSorting: false,
      cell: ({ row }) => {
        const { armProfile, isGroupHeader } = row.original;
        if (isGroupHeader) return null;
        return renderArmBadge(armProfile);
      },
    },
    {
      id: 'aiAutomationLevel',
      accessorKey: 'aiAutomationLevel',
      meta: { label: 'AI' },
      header: 'Quill Automation',
      enableSorting: false,
      cell: ({ row }) => {
        const { aiAutomationLevel, isGroupHeader } = row.original;
        if (isGroupHeader) return null;
        return renderAiBadge(aiAutomationLevel);
      },
    },
    {
      id: 'archivedAt',
      accessorKey: 'archivedAt',
      meta: { label: 'Archive Status' },
      header: 'Archive Status',
      enableSorting: false,
      cell: ({ row }) => {
        const { archivedAt, isGroupHeader } = row.original;

        // Don't show content for group headers in this column
        if (isGroupHeader) {
          return null;
        }

        return archivedAt ? (
          <span className="inline-flex items-center rounded-full bg-gray-300/20 px-2 py-1 text-xs font-semibold text-white/70">
            Archived
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-green-600/20 px-2 py-1 text-xs font-semibold text-white">
            Active
          </span>
        );
      },
    },
  ];

  // Transform data for table
  const siteData = useMemo(() => {
    if (!sites) return [];
    const baseData = extractSiteTableData(sites);

    // If grouping is enabled, add customer group rows
    if (groupByCustomer) {
      const result: SiteTableData[] = [];

      // Group sites by customer
      const grouped = baseData.reduce<Record<string, SiteTableData[]>>((acc, site) => {
        const customer = site.companyName || 'Other';
        if (!acc[customer]) {
          acc[customer] = [];
        }
        acc[customer].push(site);
        return acc;
      }, {});

      // Sort customers alphabetically
      const sortedCustomers = Object.keys(grouped).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
      );

      // Add group headers and sites
      sortedCustomers.forEach((customer) => {
        // Add customer group header
        result.push({
          id: `group-${customer}`,
          name: customer,
          companyId: '',
          companyName: customer,
          location: '',
          isActive: true,
          status: '',
          archivedAt: null,
          isGroupHeader: true,
        });

        // Sort sites within customer group
        const sortedSites = grouped[customer].sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        );

        // Add customer's sites
        result.push(...sortedSites);
      });

      return result;
    }

    return baseData;
  }, [sites, groupByCustomer]);

  // Group sites by client when needed
  const groupedSites = useMemo(() => {
    // First sort all sites alphabetically by name
    const sortedSites = [...sites].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
    );

    if (!groupByCustomer) return { ungrouped: sortedSites };

    const grouped = sortedSites.reduce<Record<string, SitesSearchDto[]>>(
      (acc, site: SitesSearchDto) => {
        const customer = site.companyName || 'Other';
        if (!acc[customer]) {
          acc[customer] = [];
        }
        acc[customer].push(site);
        return acc;
      },
      {}
    );

    // Sort each customer's sites alphabetically
    Object.keys(grouped).forEach((customer) => {
      grouped[customer].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
      );
    });

    return grouped;
  }, [sites, groupByCustomer]);

  // Render site card for grid view
  const renderSiteCard = useCallback(
    (site: SitesSearchDto) => (
      <Link
        to={`/customer/${site.companyId}/project/${site.id}`}
        key={site.id}
        className="group relative flex flex-col justify-between rounded-lg border bg-[#111] p-4 text-white transition-all duration-300 hover:border-gray-500 hover:bg-[#222]"
      >
        <div>
          <div className="mb-2 flex flex-wrap items-center justify-between">
            <h2 className="text-2xl font-semibold text-lime">{site.name}</h2>
            <div className="flex items-center gap-2">
              <div
                className={`rounded-full px-2 py-1 text-sm font-semibold ${
                  site.isActive ? 'bg-green-200/10' : 'bg-gray-800 text-gray-500'
                }`}
              >
                {site.isActive ? 'Healthy' : 'Offline'}
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-400">{site.companyName}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {renderArmBadge((site as ExtendedSite).armProfile)}
            {renderAiBadge((site as ExtendedSite).aiAutomationLevel)}
          </div>
        </div>
      </Link>
    ),
    []
  );

  // Handle filter changes (search bar etc.)
  const countFilters = useCallback(
    (values: Record<string, string | Date | undefined>) =>
      Object.values(values).filter((v) => v !== undefined && v !== '').length,
    []
  );

  const handleFiltersChange = useCallback(
    ({
      values,
    }: {
      filterOperator: FilterOperator;
      filterFilterGroups: FilterGroup[];
      values: Record<string, string | Date | undefined>;
    }) => {
      setUrlParams({
        name: (values.name as string) || null,
        armProfile: (values.armProfile as string) || null,
        aiAutomationLevel: (values.aiAutomationLevel as string) || null,
        archived: (values.archived as string) || null,
        page: 1,
      });
      setFilterCount(countFilters({
        ...values,
        archived: values.archived === 'any' ? undefined : values.archived,
      }));
    },
    [setUrlParams, countFilters]
  );

  // Handle page size change
  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setUrlParams({ pageSize: newPageSize, page: 1 });
    },
    [setUrlParams]
  );

  const initialFilterCount = useMemo(
    () =>
      countFilters({
        name: searchQuery || undefined,
        armProfile: armProfileFilter || undefined,
        aiAutomationLevel: aiAutomationFilter || undefined,
        archived: archivedFilter !== 'any' ? archivedFilter || undefined : undefined,
      }),
    [searchQuery, armProfileFilter, aiAutomationFilter, archivedFilter, countFilters]
  );

  const [filterCount, setFilterCount] = useState(initialFilterCount);
  const filterScrollHeight = 'calc(100vh - 128px)';

  return (
    <div className="relative">
      <div data-onboarding="sites-page">
      </div>

      {view === 'table' ? (
        <DataTable
          columns={siteColumns}
          data={siteData}
          loading={isLoading && !data}
          error={error}
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={(p: number) => setUrlParams({ page: p })}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={[10, 20, 50, 100]}
          defaultColumnVisibility={{archivedAt:false}}
          stateKey="sites-table"
          emptyMessage="No sites found"
          sidebarOpen={filtersOpen}
          onSidebarOpenChange={(open) => setUrlParams({ filters: open ? 'open' : 'closed' })}
        >
          <FilterForm
            defaultValues={{
              name: searchQuery || undefined,
              armProfile: armProfileFilter || undefined,
              aiAutomationLevel: aiAutomationFilter || undefined,
              archived: archivedFilter,
            }}
            onFiltersChange={handleFiltersChange}
          >
            <DataTableContainer>
              <DataTableFilterBar>
                <div className="flex h-full flex-col">
                  <FilterHeader onClose={() => setUrlParams({ filters: 'closed' })} />
                  <ScrollArea style={{ height: filterScrollHeight }} className="flex-1 px-4">
                    <div className="space-y-4 pb-4 pt-2">
                      <FilterInlineResetButton
                        onReset={() => {
                          setUrlParams({
                            name: null,
                            armProfile: null,
                            aiAutomationLevel: null,
                            archived: 'any',
                            page: 1,
                          });
                          setFilterCount(0);
                        }}
                      />
                    </div>
                    <div className="space-y-4 pb-4">
                      <FilterField
                        type="toggle"
                        name="armProfile"
                        label="Arming Status"
                        field="armProfile"
                        comparator={Comparator.Eq}
                        options={[
                          { label: 'Any', value: '' },
                          { label: <ArmBadge value={EnumArmProfile.Auto} />, value: 'Auto' },
                          { label: <ArmBadge value={EnumArmProfile.Armed} />, value: 'Armed' },
                          {
                            label: <ArmBadge value={EnumArmProfile.Disarmed} />,
                            value: 'Disarmed',
                          },
                          {
                            label: <ArmBadge value={EnumArmProfile.TestMode} />,
                            value: 'TestMode',
                          },
                        ]}
                      />
                      <FilterField
                        type="toggle"
                        name="aiAutomationLevel"
                        label="Quill Automation"
                        field="aiAutomationLevel"
                        comparator={Comparator.Eq}
                        options={[
                          { label: 'Any', value: '' },
                          { label: <AiBadge value={EnumAiAutomationLevel.None} />, value: 'None' },
                          {
                            label: <AiBadge value={EnumAiAutomationLevel.Assistant} />,
                            value: 'Assistant',
                          },
                          { label: <AiBadge value={EnumAiAutomationLevel.Full} />, value: 'Full' },
                        ]}
                      />
                      <FilterField
                        type="toggle"
                        name="archived"
                        label="Archived"
                        field="archivedAt"
                        comparator={Comparator.Eq}
                        options={[
                          { label: 'Any', value: 'any' },
                          { label: 'Active', value: 'active' },
                          { label: 'Archived', value: 'archived' },
                        ]}
                      />
                    </div>
                  </ScrollArea>
                </div>
              </DataTableFilterBar>
              <DataTableContent>
                <div data-onboarding="sites-utility-bar">
                    <DataTableUtilityBar
                      showFilterToggle
                      showColumnVisibility
                      search={{
                        value: searchQuery,
                        onChange: (value) => setUrlParams({ name: value || null, page: 1 }),
                        placeholder: 'Search sites...',
                      }}
                      filterCount={filterCount}
                      actions={
                        <Button
                          size="sm"
                          onClick={() => navigate('/site/new')}
                          data-onboarding="sites-add-button"
                        >
                          Add Site
                        </Button>
                      }
                    />
                </div>
                <div data-onboarding="sites-table">
                  <DataTableWrapper>
                    <DataTableHeader />
                    <DataTableRows />
                  </DataTableWrapper>
                </div>
                <DataTableFooter />
              </DataTableContent>
            </DataTableContainer>
          </FilterForm>
        </DataTable>
      ) : (
        <div className="flex-1 overflow-auto p-4">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <div className="text-white">Loading...</div>
            </div>
          ) : (
            <div className="">
              {Object.entries(groupedSites).map(([customer, customerSites]) => (
                <div key={customer}>
                  {groupByCustomer ? (
                    <Accordion type="single" collapsible defaultValue={customer}>
                      <AccordionItem value={customer} className="border-b-0">
                        <AccordionTrigger className="mb-4 items-center rounded-xl bg-[#222] px-4 py-3 text-xl font-bold text-lime">
                          <div className="flex items-center">
                            {customer}{' '}
                            <span className="align-center ml-2 inline-flex h-6 w-6 justify-center rounded-full bg-[#333] text-sm font-semibold tracking-normal text-gray-300">
                              {customerSites.length}
                            </span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="">
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {customerSites.map(renderSiteCard)}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  ) : (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {customerSites.map(renderSiteCard)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Sites;
